//import ./utils/validations needs to be put on top for proper custom validation checks
import './utils/validations';
import * as React from 'react';
import { useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useConsentData } from 'helpers/use-consent-data';
import { FLOWS } from './globals/routes';
import {
  getCommunityListPath,
  getCoverageExplanationUrl,
  getDivisionListPath,
  getLandingPath,
  getQuotePath,
} from './helpers/paths';
import ConfirmationFlow from './routes/confirmation-flow';
import ConsentFlow from './routes/consent-flow';
import { useDispatch } from 'react-redux';
import { fetchOptions } from './actions/consent';
import { initializeCoralogixRum } from './utils/initializeCoralogixRum';

import MissingPage from 'pages/missing/Missing';
import { ThankYou } from 'pages/thankYou/ThankYou';
import CommunitiesRatesheet from 'pages/communitiesRatesheet/CommunitiesRatesheet';
import { GetQuote } from 'pages/getQuote/GetQuote';
import StaticFlow from './routes/static-flow';
import LandingPage from './pages/landing/Landing';
import { DivisionList } from './pages/divisionList/DivisionList';
import { CommunityList } from './pages/communityList/CommunityList';
import { CoverageExplanation } from './pages/coverageExplanation/CoverageExplanation';
import { LoadingFallback } from './components/LoadingFallback';
import { ConsentData } from './reducers/consent/types';
import { getIsFlagshipEnabled } from './components/FlagshipWrapper/FlagshipWrapper';
import {
  FETCH_FLAG_STATUS,
  useFetchFlagshipFlagsBasedOnVisitorContext,
} from './helpers/use-fetch-flagship-flags-based-on-visitor-context';

initializeCoralogixRum();

const LazyFetchWithFlagship = ({ consentData }: { consentData: ConsentData }) => {
  const fetchFlagStatus = useFetchFlagshipFlagsBasedOnVisitorContext({ consentData });

  if (fetchFlagStatus === FETCH_FLAG_STATUS.SUCCESS || fetchFlagStatus === FETCH_FLAG_STATUS.ERROR) {
    return (
      <Switch>
        <Route path={FLOWS.consent} component={ConsentFlow} />
        <Route path={FLOWS.confirm} component={ConfirmationFlow} />
        <Route path={FLOWS.static} component={StaticFlow} />
        <Redirect to={'/404'} />
      </Switch>
    );
  }

  return <LoadingFallback />;
};

const FlowRoutes = () => {
  const consentData = useConsentData();
  const isFlagshipEnabled = getIsFlagshipEnabled();

  if (!consentData) {
    return <LoadingFallback />;
  }

  if (isFlagshipEnabled) {
    return <LazyFetchWithFlagship consentData={consentData} />;
  }

  return (
    <Switch>
      <Route path={FLOWS.consent} component={ConsentFlow} />
      <Route path={FLOWS.confirm} component={ConfirmationFlow} />
      <Route path={FLOWS.static} component={StaticFlow} />
      <Redirect to={'/404'} />
    </Switch>
  );
};

const quoteIdRegexStr = '[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}';
export const pathToFlows = `/(consent|confirm|static)/:quoteId(${quoteIdRegexStr})`;

export const App = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchOptions());
  }, [dispatch]);

  return (
    <React.Suspense fallback={<LoadingFallback />}>
      <Switch>
        <Route path={pathToFlows} component={FlowRoutes} />
        <Route path={getQuotePath()} component={GetQuote} />
        <Route path='/thank-you' component={ThankYou} />
        <Route exact={true} path={getLandingPath()} component={LandingPage} />

        <Route path={getDivisionListPath()} exact={true} component={DivisionList} />
        <Route path={getCommunityListPath()} component={CommunityList} />

        <Route path='/communities/:communityId/ratesheet' component={CommunitiesRatesheet} />
        <Route path={getCoverageExplanationUrl()} component={CoverageExplanation} />
        <Route exact={true} path='/404' component={MissingPage} />
        <Route exact={true} path='/500' component={MissingPage} />
        <Redirect to='/404' />
      </Switch>
    </React.Suspense>
  );
};

export default App;
